import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormGroup } from "../components/ui/form-group";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const API_URL = process.env.REACT_APP_API_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email) {
      setError("Email is required");
      return;
    }

    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);

    try {
      // Use the same endpoint as the React Native app
      const response = await fetch(`${API_URL}/api/v1/password/reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        const data = await response.json();
        setIsSubmitted(true);
        setSuccessMessage(
          data.message || "Password reset link sent successfully!",
        );
      } else {
        const data = await response.json();
        setError(data?.error || "Failed to send reset link. Please try again.");
      }
    } catch (err) {
      console.error("Forgot password error:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  if (isSubmitted) {
    return (
      <div style={styles.container}>
        <div style={styles.logoContainer}>
          <img src="/carepetai-logo.svg" alt="Logo" style={styles.logo} />
        </div>

        <div style={styles.formContainer}>
          <div style={styles.successContainer}>
            <div style={styles.successIcon}>✓</div>
            <h2 style={styles.successTitle}>Check Your Email</h2>
            <p style={styles.successMessage}>
              {successMessage ||
                `If an account exists for ${email}, you will receive a password reset link shortly.`}
            </p>
            <p style={styles.note}>
              Please check your spam folder if you don't see the email.
            </p>
            <div style={styles.buttonContainer}>
              <Button
                type="button"
                variant="primary"
                fullWidth
                size="large"
                onClick={handleBackToLogin}
              >
                Back to Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.logoContainer}>
        <img src="/carepetai-logo.svg" alt="Logo" style={styles.logo} />
      </div>

      <div style={styles.formContainer}>
        <div style={styles.headerContainer}>
          <h2 style={styles.title}>Reset Your Password</h2>
          <p style={styles.subtitle}>
            Enter your email address and we'll send you a link to reset your
            password.
          </p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <FormGroup
            label="Email"
            required
            error={error}
            hint="Enter the email associated with your account"
            marginBottom="24px"
          >
            <Input
              type="email"
              name="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              error={!!error}
              disabled={isLoading}
            />
          </FormGroup>

          <div style={styles.buttonContainer}>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              size="large"
              disabled={isLoading}
            >
              {isLoading ? "Sending Reset Link..." : "Send Reset Link"}
            </Button>
          </div>

          <div style={styles.backToLoginContainer}>
            <button
              type="button"
              onClick={handleBackToLogin}
              style={styles.backToLoginLink}
              disabled={isLoading}
            >
              ← Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: "60px 0",
    minHeight: "100vh",
    position: "relative",
    fontFamily: "'Poppins', sans-serif",
    backgroundColor: "#f9fafb",
  },
  logoContainer: {
    textAlign: "center",
    margin: "10px 0 30px 0",
  },
  logo: {
    width: "300px",
    height: "50px",
    objectFit: "contain",
  },
  formContainer: {
    padding: "40px",
    maxWidth: "480px",
    margin: "0 auto",
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
  },
  headerContainer: {
    marginBottom: "32px",
    textAlign: "center",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    marginBottom: "12px",
    color: "#111827",
    lineHeight: "1.2",
  },
  subtitle: {
    fontSize: "16px",
    color: "#6b7280",
    lineHeight: "1.5",
    margin: "0 auto",
    maxWidth: "400px",
  },
  form: {
    width: "100%",
  },
  buttonContainer: {
    marginTop: "8px",
  },
  backToLoginContainer: {
    marginTop: "24px",
    textAlign: "center",
  },
  backToLoginLink: {
    background: "none",
    border: "none",
    color: "#6b7280",
    fontSize: "15px",
    cursor: "pointer",
    padding: "8px 16px",
    fontFamily: "'Poppins', sans-serif",
    borderRadius: "6px",
    transition: "all 0.2s ease",
  },
  backToLoginLinkHover: {
    backgroundColor: "#f3f4f6",
    color: "#374151",
  },
  successContainer: {
    textAlign: "center",
    padding: "20px",
  },
  successIcon: {
    width: "64px",
    height: "64px",
    backgroundColor: "#10b981",
    color: "white",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "32px",
    fontWeight: "bold",
    margin: "0 auto 24px auto",
  },
  successTitle: {
    fontSize: "24px",
    fontWeight: "700",
    marginBottom: "16px",
    color: "#111827",
  },
  successMessage: {
    fontSize: "16px",
    color: "#374151",
    lineHeight: "1.5",
    marginBottom: "12px",
  },
  note: {
    fontSize: "14px",
    color: "#6b7280",
    fontStyle: "italic",
    lineHeight: "1.5",
  },
};

// Add hover effect
styles.backToLoginLink["&:hover"] = styles.backToLoginLinkHover;

export default ForgotPasswordPage;
